/**
 * 저장 버전 마이그레이션 — 선언형 **버전 범위 룰 테이블** 엔진.
 *
 * 호출자(앱 SaveSystem)는 "저장 버전 v를 어느 변환으로 보낼지"를 imperative `if` 사슬로 쌓는 대신
 * `{ range, migrate }` 룰 배열로 선언한다. `migrateSave`가 raw 객체의 version을 읽어 매칭 룰을 골라
 * 변환을 위임한다. 새 버전 추가 = 룰 행 1개(또는 기존 범위 max 증가).
 *
 * 본 커널은 **버전 디스패치만** 담당한다 — 실제 필드 변환(타입 강제·기본값·재산정)은 각 룰의 migrate가
 * 소유한다(도메인 지식은 앱에 둔다 — 패키지는 도메인 타입을 모른다, DIP).
 */

/** 저장 버전 범위 (min·max 둘 다 inclusive). */
export interface SaveVersionRange {
  readonly min: number;
  readonly max: number;
}

/**
 * 한 마이그레이션 룰 — savedVersion이 `range`에 들면 `migrate`가 정규화 결과를 만든다.
 * @typeParam T 정규화 후 세이브 데이터 타입.
 */
export interface SaveMigrationRule<T> {
  /** 적용 대상 저장 버전 범위 (inclusive). 룰 간 범위는 겹치지 않게 정의할 것(겹치면 첫 매칭 사용). */
  readonly range: SaveVersionRange;
  /**
   * raw 파싱 객체 + 저장 버전 → 정규화 데이터.
   * 같은 범위 안에서도 `savedVersion`으로 세부 분기 가능 (예: 현재 버전 reload는 보존, 구버전은 재산정).
   */
  readonly migrate: (raw: Record<string, unknown>, savedVersion: number) => T;
}

/** `min`~`max`(inclusive) 범위 룰용 헬퍼. */
export const versionRange = (min: number, max: number): SaveVersionRange => ({ min, max });

/** 단일 버전 룰용 헬퍼 — `versionRange(v, v)`. */
export const atVersion = (v: number): SaveVersionRange => ({ min: v, max: v });

/**
 * raw 세이브를 버전 룰 테이블로 정규화한다.
 * - raw가 객체가 아니거나 version 필드가 number가 아니면 `null`(폐기 → 새 게임).
 * - savedVersion에 매칭되는 룰이 없으면 `null`(지원 안 하는 구버전 폐기).
 * - 매칭 룰이 여럿이면 **테이블 정의 순서상 첫 매칭**을 쓴다.
 *
 * @param versionKey raw에서 버전을 읽을 키 (기본 `"version"`).
 */
export function migrateSave<T>(
  raw: unknown,
  rules: readonly SaveMigrationRule<T>[],
  options?: { readonly versionKey?: string },
): T | null {
  if (typeof raw !== "object" || raw === null) return null;
  const obj = raw as Record<string, unknown>;
  const v = obj[options?.versionKey ?? "version"];
  if (typeof v !== "number") return null;
  const rule = rules.find((r) => v >= r.range.min && v <= r.range.max);
  return rule ? rule.migrate(obj, v) : null;
}
